// @flow
import type {
  $Scope,
  AngularInjector,
  AngularLinkFunction,
  AngularModule,
  CompileFunction,
  JqliteElement,
} from 'angular';

import angular from 'angular';
import React from 'react';

type ScopeMixin = { [string]: any };
type ReactNgProps = {
  /**
   * The angular template string to render.
   */
  children: string,
  /**
   * An object to mix into the scope object. Use this to pass values to the
   * angular template.
   */
  scope: ScopeMixin,
  /**
   * By default, ReactNg will extend the root scope of your `ng-app`-mounted
   * app. If you call `angular.bootstrap()` manually, you can provide the
   * injector function returned by `angular.bootstrap()` here instead.
   */
  injector?: AngularInjector, // eslint-disable-line react/require-default-props
};

class ReactNg extends React.Component<ReactNgProps> {
  componentDidMount() {
    this.useInjector(this.props.injector);
    this.compileAngular(this.props.children, this.props.scope);
  }
  componentDidUpdate(prevProps: ReactNgProps) {
    let injectorDidChange = false;
    if (prevProps.injector !== this.props.injector) {
      this.useInjector(this.props.injector);
      injectorDidChange = true;
    }
    if (injectorDidChange || prevProps.children !== this.props.children) {
      this.compileAngular(this.props.children, this.props.scope);
    } else if (prevProps.scope !== this.props.scope) {
      this.updateScopeMixin(prevProps.scope, this.props.scope);
    }
  }

  get ngMountContainer(): JqliteElement {
    if (!this.ngMountContainerRef || !this.ngMountContainerRef.current) {
      throw new Error(
        'Cannot access the ngMountContainer because the react' +
          ' container for it has not yet mounted.',
      );
    }
    return angular.element(this.ngMountContainerRef.current);
  }

  ngMountContainerRef = React.createRef();
  $compile: ?CompileFunction;
  $rootScope: $Scope<any>;
  activeScope: $Scope<ScopeMixin>;
  rootModule: AngularModule;

  createScope(scopeMixin: ScopeMixin) {
    const scope = this.$rootScope.$new(true);
    Object.assign(scope, scopeMixin);
    return scope;
  }
  compileAngular(template: string, scopeMixin: ScopeMixin) {
    this.ngMountContainer.empty();
    if (!this.$compile) {
      throw new Error('No angular app found when ReactNg mounted!');
    }
    const linkFn: AngularLinkFunction = this.$compile(template);
    this.activeScope = this.createScope(scopeMixin);
    const element: JqliteElement = linkFn(this.activeScope);
    const shouldDigest = !['$digest', '$apply'].includes(this.$rootScope.$$phase);
    if (shouldDigest) {
      this.$rootScope.$digest();
    }
    this.ngMountContainer.append(element);
  }
  updateScopeMixin(oldScope: ScopeMixin, newScope: ScopeMixin) {
    // first, remove our existing bindings:
    for (const key of Object.keys(oldScope)) {
      delete this.activeScope[key];
    }
    Object.assign(this.activeScope, newScope);
    const shouldDigest = !['$digest', '$apply'].includes(this.$rootScope.$$phase);
    if (shouldDigest) {
      this.$rootScope.$digest();
    }
  }
  useInjector(providedInjector?: AngularInjector) {
    let injector: AngularInjector;
    if (providedInjector) {
      injector = providedInjector;
    } else {
      const appElement = document.querySelector('[ng-app]');
      if (appElement) {
        injector = angular.element(appElement).injector();
      } else {
        throw new Error(
          'ReactNg requires either an angular app to be' +
            ' mounted via ng-app, or an injector prop',
        );
      }
    }
    this.$compile = injector.get('$compile');
    this.$rootScope = injector.get('$rootScope');
  }
  render() {
    return <div ref={this.ngMountContainerRef} />;
  }
}
type ReactNgOptions = {
  injector: AngularInjector,
};

/**
 * Returns a tagged template literal function, which, in turn, will return
 * a `<ReactNg />` component instance. The `scope` and `injector`
 * arguments correspond to that component's props with the same name. The
 * template string will be used as the `children` prop. See `ReactNg` for
 * details on how these are used.
 */
export default function reactNg(scope: ScopeMixin, { injector }: ReactNgOptions = {}) {
  return function reactNgTemplate(template: Array<string>, ...substitutions: Array<any>) {
    return (
      <ReactNg scope={scope} injector={injector}>
        {/* $FlowFixMe - String.raw is broken https://github.com/facebook/flow/issues/2616 */}
        {String.raw(template, ...substitutions)}
      </ReactNg>
    );
  };
}
